import axios from 'axios';
import config from '../../config/config';
import { getEndpoint, getApiUrl } from '../../config/config';
import { showError } from '../../utils/toast';
import { TOAST_DURATIONS } from '../../constants/toastConstants';
import { queryClient } from './queryClient';

export const apiClient = axios.create({
  baseURL: config.API.BASE_URL,
  timeout: config.API.TIMEOUT || 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise = null;

export const classifyRefreshError = (error) => {
  const statusCode = error.response?.status;
  const errorCode = error.code;
  
  if (statusCode === 401) {
    return {
      type: 'AUTH_ERROR',
      shouldLogout: true,
      retryable: false,
      message: 'Refresh token expired or invalid',
      statusCode: 401
    };
  }
  
  if (statusCode === 403) {
    return {
      type: 'FORBIDDEN_ERROR',
      shouldLogout: true,
      retryable: false,
      message: 'Access forbidden',
      statusCode: 403
    };
  }
  
  if (errorCode === 'ERR_NETWORK' || errorCode === 'ECONNABORTED') {
    return {
      type: errorCode === 'ERR_NETWORK' ? 'NETWORK_ERROR' : 'TIMEOUT_ERROR',
      shouldLogout: false,
      retryable: true,
      message: errorCode === 'ERR_NETWORK' 
        ? 'Network error. Please check your connection and try again.'
        : 'Request timeout. Please try again.',
      statusCode: null
    };
  }
  
  if (statusCode >= 500) {
    return {
      type: 'SERVER_ERROR',
      shouldLogout: false,
      retryable: true,
      message: 'Server error. Please try again later.',
      statusCode: statusCode
    };
  }
  
  if (statusCode >= 400 && statusCode < 500) {
    return {
      type: 'CLIENT_ERROR',
      shouldLogout: true,
      retryable: false,
      message: error.response?.data?.error || `Token refresh failed (${statusCode})`,
      statusCode: statusCode
    };
  }
  
  return {
    type: 'UNKNOWN_ERROR',
    shouldLogout: true,
    retryable: false,
    message: error.message || 'Token refresh failed',
    statusCode: null
  };
};

export const handleRefreshError = (error, performLogout) => {
  const classification = classifyRefreshError(error);
  
  console.error('❌ Token refresh error:', error);
  
  const warningMessages = {
    NETWORK_ERROR: '🌐 Network error during token refresh - user can retry the request',
    TIMEOUT_ERROR: '🌐 Network error during token refresh - user can retry the request',
    SERVER_ERROR: '⚠️ Server error during token refresh - user can retry the request',
    FORBIDDEN_ERROR: '🔐 Forbidden error during token refresh - logging out',
    CLIENT_ERROR: `⚠️ Client error (${classification.statusCode}) during token refresh - logging out`,
    UNKNOWN_ERROR: '⚠️ Unknown error during token refresh - logging out'
  };
  
  const warning = warningMessages[classification.type];
  if (warning) console.warn(warning);
  
  if (classification.shouldLogout && performLogout) {
    performLogout(classification.message).catch(() => {});
  }
  
  return {
    ...classification,
    error
  };
};

let isLoggingOut = false;
let isManualLogout = false;

// LocalStorage keys for auth data (shared with AuthContext)
export const AUTH_STORAGE_KEYS = [
  'access_token',
  'refresh_token',
  'user',
  'current_tenant',
  'available_tenants'
];

// Store navigation function for React Router navigation
let navigateFunction = null;

export const setNavigateFunction = (navigate) => {
  navigateFunction = navigate;
};

// Export function to set manual logout flag
export const setManualLogoutFlag = (value) => {
  isManualLogout = value;
};

const redirectToLogin = () => {
  // Use React Router navigation if available (no page reload)
  if (navigateFunction && typeof navigateFunction === 'function') {
    try {
      navigateFunction('/login', { replace: true });
      return;
    } catch (error) {
      console.warn('Failed to use React Router navigation, falling back to window.location:', error);
    }
  }
  
  // Fallback to window.location for non-React Router scenarios (e.g., token expiration)
  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  } else {
    window.location.reload();
  }
};

const getLogoutMessage = (reason) => {
  if (!reason || typeof reason !== 'string') {
    // return 'Your session has expired. Please log in again.';
    return;
  }
  
  const expiredKeywords = ['expired', 'invalid', 'No refresh token', 'Session expired'];
  const forbiddenKeywords = ['forbidden', 'Access forbidden'];
  
  if (expiredKeywords.some(keyword => reason.includes(keyword))) {
    // return 'Your session has expired. Please log in again.';
    return;
  }
  
  if (forbiddenKeywords.some(keyword => reason.includes(keyword))) {
    return 'Access denied. Please log in again.';
  }
  
  return `${reason}. Please log in again.`;
};

/**
 * Disconnect all WebSocket services
 * Used by both manual logout and automatic logout flows
 */
export const disconnectWebSockets = async () => {
  try {
    const [tenantWS, userWS, ws] = await Promise.all([
      import('../../services/tenantWebSocketService').then(m => m.default).catch(() => null),
      import('../../services/userWebSocketService').then(m => m.default).catch(() => null),
      import('../../services/websocketService').then(m => m.default).catch(() => null)
    ]);
    
    [tenantWS, userWS, ws].forEach(service => {
      if (service?.disconnect) {
        try {
          service.disconnect();
        } catch {
          // Silently fail - don't block logout
        }
      }
    });
  } catch {
    // Silently fail - don't block logout
  }
};

const performLogout = async (reason = 'Session expired', showNotification = true) => {
  if (isLoggingOut) return;
  isLoggingOut = true;

  try {
    console.warn(`🔐 Logging out: ${reason}`);

    // Call logout API for automatic session expiration (not for manual logout)
    // Manual logout is handled by authService.logout() which calls performLogout after API call
    if (!isManualLogout) {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          await axios.post(
            getApiUrl('AUTH', 'LOGOUT'),
            {},
            {
              baseURL: config.API.BASE_URL,
              timeout: 5000, // Short timeout to avoid blocking
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          console.log('✅ Automatic logout API called successfully');
        } catch (error) {
          // Don't block logout flow if API call fails
          console.warn('⚠️ Automatic logout API call failed (continuing with logout):', error.message);
        }
      }
    }

    // Don't show notification if it's a manual logout or if notification is disabled
    if (showNotification && !isManualLogout) {
      console.log('manual logout', isManualLogout);
      showError(getLogoutMessage(reason), {
        duration: TOAST_DURATIONS.MEDIUM,
      });
    }

    delete apiClient.defaults.headers.common['Authorization'];
    AUTH_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));

    // Clear chat conversations store (All/My/Inbox/Alerts tab lists) so next login gets fresh data
    try {
      const { useConversationsStore } = await import('../../components/chat/hooks/FolderUseChat/conversationsStore');
      useConversationsStore.getState().clearAll();
    } catch (e) {
      console.warn('Conversations store clear on logout:', e?.message);
    }
    localStorage.removeItem('chat_selected_chat_id');

    // Clear React Query cache and reset to default state
    // This ensures no cached data from previous tenant/user persists
    queryClient.clear();
    queryClient.resetQueries();

    await disconnectWebSockets();
    refreshPromise = null;
    
    console.log('✅ React Query cache cleared and reset during logout');

    // For automatic logouts (session expired), use shorter delay and React Router navigation
    // For manual logouts, don't redirect (component handles navigation)
    if (!isManualLogout) {
      const redirectDelay = showNotification ? 2000 : 100;
      setTimeout(() => {
        redirectToLogin();
        isLoggingOut = false;
        isManualLogout = false; // Reset flag
      }, redirectDelay);
    } else {
      // Manual logout - reset flags, component handles navigation
      isLoggingOut = false;
      isManualLogout = false;
    }
  } catch (error) {
    console.error('❌ Error during logout:', error);
    try {
      if (!isManualLogout) {
        redirectToLogin();
      }
    } catch {
      console.error('❌ Critical: Could not redirect to login page');
    }
    isLoggingOut = false;
    isManualLogout = false; // Reset flag
  }
};

export const performTokenRefresh = async (refreshToken) => {
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const refreshEndpoint = getEndpoint('AUTH', 'REFRESH_TOKEN');
  if (!refreshEndpoint) {
    throw new Error('Refresh token endpoint not configured');
  }

  const response = await axios.post(
    refreshEndpoint,
    { refresh: refreshToken },
    {
      baseURL: config.API.BASE_URL,
      timeout: config.API.TIMEOUT || 30000,
      headers: { 'Content-Type': 'application/json' }
    }
  );

  const payload = response.data?.data ?? response.data;
  const access = payload?.access;
  if (!access) {
    throw new Error('No access token in refresh response');
  }

  return {
    access,
    refresh: payload?.refresh || refreshToken
  };
};

apiClient.interceptors.request.use(
  (config) => {
    const currentToken = tokenManager.getCurrentToken();
    if (currentToken && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip token refresh for authentication endpoints (login, signup, etc.)
    // These endpoints are for unauthenticated users and should not trigger refresh
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.skipAuthRefresh) {
      if (refreshPromise) {
        return refreshPromise
          .then(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch(refreshError => Promise.reject(refreshError));
      }

      originalRequest._retry = true;
      
      refreshPromise = (async () => {
        try {
          const refreshToken = tokenManager.getCurrentRefreshToken();
          if (!refreshToken) {
            throw new Error('No refresh token available');
          }
          
          const { access: newAccessToken, refresh: newRefreshToken } = await performTokenRefresh(refreshToken);
          tokenManager.setTokens(newAccessToken, newRefreshToken);
          return newAccessToken;
        } catch (refreshError) {
          handleRefreshError(refreshError, performLogout);
          throw refreshError;
        } finally {
          setTimeout(() => { refreshPromise = null; }, 0);
        }
      })();

      try {
        const newAccessToken = await refreshPromise;
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export const tokenManager = {
  setAuthToken(token) {
    if (token) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('access_token', token);
    } else {
      delete apiClient.defaults.headers.common['Authorization'];
      localStorage.removeItem('access_token');
    }
  },

  setRefreshToken(refreshToken) {
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    } else {
      localStorage.removeItem('refresh_token');
    }
  },

  setTokens(accessToken, refreshToken) {
    this.setAuthToken(accessToken);
    this.setRefreshToken(refreshToken);
  },

  clearAuthToken() {
    delete apiClient.defaults.headers.common['Authorization'];
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },

  getCurrentToken() {
    return localStorage.getItem('access_token');
  },

  getCurrentRefreshToken() {
    return localStorage.getItem('refresh_token');
  },

  updateAxiosHeaders() {
    const currentToken = this.getCurrentToken();
    if (currentToken) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
    } else {
      delete apiClient.defaults.headers.common['Authorization'];
    }
  },
};

export { performLogout as logout };

export default apiClient;

